import React, { useEffect, useState } from "react";
import { Outlet, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import Sidebar from "../sidebar/Sidebar";
import Topbar from "../topbar/Topbar";
import { getCurrentUser } from "../../services/userServices";
import { UserContext } from "../../context/UserContext";
import styles from "./Layout.module.css";

function normalizeUser(data) {
  if (!data) return null;
  const fullName =
    data.name ||
    data.user_name ||
    [data.firstName, data.lastName].filter(Boolean).join(" ").trim();
  return {
    user_id: data.user_id,
    email: data.email,
    role: data.role,
    company_id: data.company_id,
    name: fullName,
    phone_number: data.phone_number,
    avatar_url: data.avatar_url,
    job_title: data.job_title,
    email_notifications: data.email_notifications,
    project_updates: data.project_updates,
    milestone_reminders: data.milestone_reminders,
    company_name: data.company_name,
    company_logo_url: data.logo_url,
  };
}

export default function Layout() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const fetchUser = async () => {
    try {
      const raw = await getCurrentUser();
      const u = normalizeUser(raw);
      setUser(u);
      return u;
    } catch (e) {
      console.error("Failed to load user:", e);
      throw e;
    }
  };

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        await fetchUser();
      } catch (e) {
        // Error already logged in fetchUser
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  if (loading) {
    return (
      <div className={styles.wrapper}>
        <div className={styles.loading}>Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.topbar}>
        <Topbar user={user} />
      </div>
      <div
        className={`${styles.main} ${
          sidebarCollapsed ? styles.sidebarCollapsed : ""
        }`}
      >
        <div className={styles.sidebarWrapper}>
          <Sidebar
            collapsed={sidebarCollapsed}
            onToggle={() => setSidebarCollapsed((prev) => !prev)}
            user={user}
            onLogout={handleLogout}
          />
        </div>

        <div className={styles.content}>
          <UserContext.Provider value={{ user, refreshUser: fetchUser }}>
            <Outlet />
          </UserContext.Provider>
        </div>
      </div>
    </div>
  );
}
